import React, { useMemo, useState } from "react";
import {
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  FileAddOutlined,
  MinusCircleFilled,
} from "@ant-design/icons";

/*
  Dynamic Attendance Calendar — matches the reference design exactly.
  ----------------------------
  Pass `attendanceData` as an object keyed by "YYYY-MM-DD" -> "present" | "leave" | "absent" | "weekoff"
  Any day not explicitly marked "leave" or "absent" is treated as "present" by default.
  Sundays default to "weekoff" automatically unless overridden in attendanceData.

  Layout (single row, no title):
    [Month nav + Today]  .............................  [Summary chips]
    Calendar grid
    Legend footer
*/

const STATUS_CONFIG = {
  present: {
    label: "Present",
    color: "#16a34a",
    bg: "#ffffff",
    icon: CheckCircleFilled,
    desc: "You were marked present on this day.",
  },
  leave: {
    label: "Leave",
    color: "#2563eb",
    bg: "#eff6ff",
    border: "#93c5fd",
    icon: FileAddOutlined,
    desc: "You had applied for leave on this day.",
  },
  absent: {
    label: "Absent",
    color: "#dc2626",
    bg: "#fef2f2",
    icon: MinusCircleFilled,
    desc: "You were marked absent on this day.",
  },
  weekoff: {
    label: "Weekly Off",
    color: "#6b7280",
    bg: "#f3f4f6",
    icon: MinusCircleFilled,
    desc: "Sunday is your weekly off.",
  },
};

const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function pad(n) {
  return String(n).padStart(2, "0");
}

function toKey(year, month, day) {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

function getStatus(attendanceData, year, month, day) {
  const key = toKey(year, month, day);
  if (attendanceData[key]) return attendanceData[key];
  const dow = new Date(year, month, day).getDay(); // 0 = Sunday
  if (dow === 0) return "weekoff";
  return "present"; // default: any day not explicitly marked leave/absent is present
}

// Build a Mon-Sun grid for the given month, including leading blanks
function buildCalendarGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = (firstDay.getDay() + 6) % 7; // Monday = 0

  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  return weeks;
}

// Sample data matching the reference design (June 2026)
const SAMPLE_ATTENDANCE_DATA = {
  "2026-06-05": "leave",
  "2026-06-08": "absent",
  "2026-06-16": "leave",
  "2026-06-21": "absent",
};

export default function AttendanceCalendar({
  attendanceData = SAMPLE_ATTENDANCE_DATA,
  initialDate = new Date(2026, 5, 1),
}) {
  const [viewDate, setViewDate] = useState(
    new Date(initialDate.getFullYear(), initialDate.getMonth(), 1)
  );

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const weeks = useMemo(() => buildCalendarGrid(year, month), [year, month]);
  const monthLabel = viewDate.toLocaleString("en-US", { month: "long", year: "numeric" });

  const today = new Date();

  const summary = useMemo(() => {
    const counts = { present: 0, leave: 0, absent: 0, weekoff: 0 };
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    for (let d = 1; d <= daysInMonth; d++) {
      const status = getStatus(attendanceData, year, month, d);
      if (status && counts[status] !== undefined) counts[status] += 1;
    }
    return counts;
  }, [attendanceData, year, month]);

  const goPrev = () => setViewDate(new Date(year, month - 1, 1));
  const goNext = () => setViewDate(new Date(year, month + 1, 1));
  const goToday = () => setViewDate(new Date(today.getFullYear(), today.getMonth(), 1));

  return (
    <div
      style={{
        fontFamily: "'Segoe UI', Roboto, Arial, sans-serif",
        background: "#f8f9fb",
        padding: "12px 16px",
        borderRadius: 16,
        maxWidth: 1500,
        margin: "0 auto",
        boxSizing: "border-box",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Single header row: nav+Today on the left, summary chips on the right — no title, no extra wrapper */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 10,
          marginBottom: 10,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 10,
              padding: "6px 12px",
              flexShrink: 0,
            }}
          >
            <button onClick={goPrev} style={iconBtnStyle} aria-label="Previous month">
              <LeftOutlined style={{ fontSize: 14 }} />
            </button>
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontWeight: 600,
                color: "#111827",
                fontSize: 14,
                minWidth: 120,
                justifyContent: "center",
                whiteSpace: "nowrap",
              }}
            >
              <CalendarOutlined style={{ fontSize: 14, color: "#6b7280" }} />
              {monthLabel}
            </span>
            <button onClick={goNext} style={iconBtnStyle} aria-label="Next month">
              <RightOutlined style={{ fontSize: 14 }} />
            </button>
          </div>

          <button
            onClick={goToday}
            style={{
              background: "#eff6ff",
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              borderRadius: 10,
              padding: "7px 14px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}
          >
            Today
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: 20,
            flexWrap: "wrap",
            justifyContent: "flex-end",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: "8px 18px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
          }}
        >
          {Object.keys(STATUS_CONFIG).map((key, idx) => {
            const cfg = STATUS_CONFIG[key];
            const isLast = idx === Object.keys(STATUS_CONFIG).length - 1;
            return (
              <div
                key={key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  borderRight: isLast ? "none" : "1px solid #e5e7eb",
                  paddingRight: isLast ? 0 : 18,
                }}
              >
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: cfg.color,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div style={{ fontWeight: 600, color: "#111827", fontSize: 13 }}>{cfg.label}</div>
                  <div style={{ color: "#6b7280", fontSize: 12 }}>{summary[key]} Days</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ overflowX: "auto", flex: 1, minHeight: 0 }}>
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
            minWidth: 560,
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid #e5e7eb", flexShrink: 0, borderTopLeftRadius: 12, borderTopRightRadius: 12, overflow: "hidden" }}>
            {WEEK_DAYS.map((d) => (
              <div
                key={d}
                style={{
                  textAlign: "center",
                  padding: "8px 0",
                  fontWeight: 600,
                  fontSize: 13,
                  color: d === "Sun" ? "#dc2626" : "#374151",
                }}
              >
                {d}
              </div>
            ))}
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
            {weeks.map((week, wi) => (
              <div
                key={wi}
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, 1fr)",
                  borderBottom: wi === weeks.length - 1 ? "none" : "1px solid #e5e7eb",
                  flex: 1,
                }}
              >
                {week.map((day, di) => {
                  if (!day) {
                    return <div key={di} style={{ borderRight: di === 6 ? "none" : "1px solid #e5e7eb" }} />;
                  }
                  const status = getStatus(attendanceData, year, month, day);
                  const cfg = status ? STATUS_CONFIG[status] : null;
                  const Icon = cfg ? cfg.icon : null;
                  const isLeave = status === "leave";

                  const numberColor =
                    status === "weekoff" || status === "absent" || status === "leave"
                      ? cfg.color
                      : "#111827";

                  return (
                    <div
                      key={di}
                      style={{
                        borderRight: di === 6 ? "none" : "1px solid #e5e7eb",
                        background: cfg ? cfg.bg : "#fff",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 2,
                        boxSizing: "border-box",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          padding: 4,
                          borderRadius: 8,
                          border: isLeave ? `1.5px solid ${cfg.border}` : "1.5px solid transparent",
                          boxSizing: "border-box",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            top: 2,
                            left: 6,
                            fontWeight: 700,
                            fontSize: 12,
                            color: numberColor,
                          }}
                        >
                          {day}
                        </div>
                        {cfg && (
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Icon style={{ fontSize: 16, color: cfg.color }} />
                            <span style={{ fontSize: 11, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "10px 20px",
          marginTop: 10,
          flexShrink: 0,
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        {Object.keys(STATUS_CONFIG).map((key) => {
          const cfg = STATUS_CONFIG[key];
          const Icon = cfg.icon;
          return (
            <div key={key} style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 200 }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  background: cfg.bg === "#ffffff" ? "#f0fdf4" : cfg.bg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Icon style={{ fontSize: 13, color: cfg.color }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, color: "#111827", fontSize: 13 }}>{cfg.label}</div>
                <div style={{ color: "#6b7280", fontSize: 11, lineHeight: 1.3 }}>{cfg.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const iconBtnStyle = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#374151",
  padding: 4,
};
